import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import CalculationDetails from "./CalculationDetails";
import { localDutyPreview } from "@/lib/qldDutyPreview";

const qro = "https://qro.qld.gov.au/duties/transfer-duty/calculate/";
const eligibility =
  "https://qro.qld.gov.au/duties/transfer-duty/concessions/homes/eligibility-tester/";
export const fields = [
  ["dutiable_value", "Confirmed dutiable value (AUD)"],
  ["deposit", "Total deposit contribution (AUD, including any already paid)"],
  ["legal_costs", "Conveyancing and legal costs (AUD)"],
  ["inspections", "Building, pest and other inspections (AUD)"],
  ["registration_fees", "Transfer and mortgage registration fees (AUD)"],
  ["lender_fees", "Lender fees paid in cash (AUD)"],
  ["lmi", "Lenders mortgage insurance paid in cash (AUD)"],
  ["other_costs", "Other acquisition costs, excluding duty (AUD)"],
  ["contingency", "Purchase cash contingency (AUD)"],
];
const categories = {
  general: "General rates — no concession claimed",
  home: "Home concession — eligibility confirmed",
  first_established: "First established home — eligibility confirmed",
  first_new: "First new home — eligibility confirmed",
  first_land: "First home vacant land — eligibility confirmed",
};
const allowed = {
  established: ["general", "home", "first_established"],
  new_home: ["general", "home", "first_new"],
  vacant_land: ["general", "first_land"],
};
export const resultLabels = {
  purchase_price: "Purchase price",
  dutiable_value: "Dutiable value used for duty",
  base_duty: "Transfer duty before foreign surcharge",
  afad: "Additional foreign acquirer duty",
  total_duty: "Total transfer duty and foreign surcharge",
  legal_costs: "Conveyancing and legal costs",
  inspections: "Inspections",
  registration_fees: "Registration fees",
  lender_fees: "Lender fees",
  lmi: "Lenders mortgage insurance",
  other_costs: "Other acquisition costs",
  acquisition_costs: "Total acquisition costs including duty",
  deposit: "Total deposit contribution",
  loan_amount: "Purchase price less deposit",
  cash_before_contingency: "Cash required before contingency",
  contingency: "Cash contingency",
  cash_needed: "Total cash required including contingency",
};
const money = (value) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(
    value,
  );

export default function PurchaseCostPanel({
  price,
  propertyType,
  onResult,
  initialInputs,
}) {
  const [values, setValues] = useState({
    transaction_type: propertyType === "land" ? "vacant_land" : "",
    category: "",
    residency: "unsure",
    ...initialInputs,
    afad_status: "",
    simple_transaction_confirmed: false,
    dutiable_value_confirmed: false,
    eligibility_confirmed: false,
  });
  const [result, setResult] = useState(null);
  const [dutyPreview, setDutyPreview] = useState(null);
  const [dutyBusy, setDutyBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const quickDutyReady = Boolean(values.transaction_type && values.contract_date && values.category && ["applies", "not_applies"].includes(values.afad_status));
  useEffect(() => {
    let active = true;
    if (!quickDutyReady || propertyType === "house_and_land") {
      setDutyPreview(null);
      setDutyBusy(false);
      return () => { active = false; };
    }
    setDutyBusy(true);
    api.post("/research/duty-preview", {
      price: String(price), property_type: propertyType,
      transaction_type: values.transaction_type, contract_date: values.contract_date,
      category: values.category, afad_status: values.afad_status,
    }).then(({ data }) => { if (active) setDutyPreview(data); })
      .catch(() => { if (active) setDutyPreview(localDutyPreview({ price, category: values.category, contractDate: values.contract_date, afadStatus: values.afad_status })); })
      .finally(() => { if (active) setDutyBusy(false); });
    return () => { active = false; };
  }, [price, propertyType, quickDutyReady, values.transaction_type, values.contract_date, values.category, values.afad_status]);
  function update(event) {
    const { name, type, checked, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
      ...([
        "category",
        "contract_date",
        "residency",
        "transaction_type",
        "dutiable_value",
        "afad_status",
      ].includes(name)
        ? { eligibility_confirmed: false }
        : {}),
      ...(name === "transaction_type" ? { category: "" } : {}),
      ...(name === "dutiable_value" ? { dutiable_value_confirmed: false } : {}),
    }));
    setResult(null);
    onResult(null);
    setError("");
  }
  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    onResult(null);
    try {
      // Keep money as decimal strings; the server performs all arithmetic.
      const { data } = await api.post("/research/purchase-costs", {
        ...values,
        price: String(price),
        property_type: propertyType,
      });
      const completed = { ...data, calculated_at: new Date().toISOString() };
      setResult(completed);
      onResult(completed);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(
        Array.isArray(detail)
          ? detail[0]?.msg?.replace(/^Value error, /, "")
          : "Purchase costs could not be calculated. Check your inputs and try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (propertyType === "house_and_land")
    return (
      <aside className="notice">
        Linked house-and-land duty and construction costs are not assessed.
        Confirm the contracts and dutiable value with your conveyancer.{" "}
        <a href={qro}>Check with QRO</a>.
      </aside>
    );
  const concession = values.category && values.category !== "general";
  return (
    <details
      id="purchase-calculator"
      className="research-card calculator-panel"
      open={initialInputs ? true : undefined}
    >
      <summary>Estimate stamp duty and purchase costs</summary>
      <p>
        Purchase price: {money(price)}. Enter quotes or your own estimates,
        including 0 where a fee does not apply. Completed calculations can be
        included when you choose to save financial scenarios with your report.
      </p>
      <form onSubmit={submit} aria-label="Purchase costs">
        <fieldset disabled={busy} className="purchase-fields">
          <legend>Confirm the transaction</legend>
          <label className="checkbox">
            <input
              required
              type="checkbox"
              name="simple_transaction_confirmed"
              checked={values.simple_transaction_confirmed}
              onChange={update}
            />
            One individual is buying the whole Queensland residential property
            in one unlinked transaction, with no trust, mixed use, additional
            non-residential land or other buyers.
          </label>
          <p className="source">
            Joint buyers, mixed entitlements and linked contracts need the{" "}
            <a href={qro}>QRO calculator or a conveyancer</a>.
          </p>
          <div className="form-grid">
            <label>
              Transaction type
              <select
                required
                name="transaction_type"
                value={values.transaction_type}
                onChange={update}
              >
                <option value="">Choose a transaction</option>
                {propertyType === "land" ? (
                  <option value="vacant_land">
                    Residential vacant land only
                  </option>
                ) : (
                  <>
                    <option value="established">Established home</option>
                    <option value="new_home">
                      New home or off-the-plan, one contract
                    </option>
                  </>
                )}
              </select>
            </label>
            <label>
              Contract date (actual or proposed)
              <input
                required
                type="date"
                min="2024-06-09"
                name="contract_date"
                value={values.contract_date || ""}
                onChange={update}
              />
            </label>
          </div>
          <label>
            Duty category
            <select
              required
              name="category"
              value={values.category}
              onChange={update}
            >
              <option value="">Choose after checking eligibility</option>
              {(allowed[values.transaction_type] || []).map((key) => (
                <option key={key} value={key}>
                  {categories[key]}
                </option>
              ))}
            </select>
          </label>
          {concession && values.contract_date >= "2026-08-01" && (
            <label>
              Residency status at contract date
              <select
                name="residency"
                value={values.residency}
                onChange={update}
              >
                <option value="unsure">Not yet confirmed</option>
                <option value="citizen">Australian citizen</option>
                <option value="permanent_resident">
                  Australian permanent resident
                </option>
                <option value="specified_foreign_retiree">
                  Specified foreign retiree under QRO rules
                </option>
                <option value="other">Other status</option>
              </select>
            </label>
          )}
          {values.category && values.category !== "general" && (
            <>
              <p>
                Confirm all conditions, including prior ownership, age,
                occupation deadlines, property use and any new-home evidence.{" "}
                <a href={eligibility}>Check full QRO concession eligibility</a>.
                Choosing a category does not establish entitlement.
              </p>
              <label className="checkbox">
                <input
                  required
                  type="checkbox"
                  name="eligibility_confirmed"
                  checked={values.eligibility_confirmed}
                  onChange={update}
                />
                I have verified eligibility for this concession for the whole
                purchase using QRO guidance or my conveyancer, including ongoing
                requirements.
              </label>
            </>
          )}
          <label>
            Additional foreign acquirer duty (AFAD)
            <select
              required
              name="afad_status"
              value={values.afad_status}
              onChange={update}
            >
              <option value="">Choose after checking QRO rules</option>
              <option value="not_applies">Confirmed not applicable</option>
              <option value="applies">Applies to the whole acquisition</option>
              <option value="unknown">
                Not yet confirmed — calculation unavailable
              </option>
            </select>
          </label>
          <p className="source">
            AFAD is assessed separately from concession eligibility.{" "}
            <a href={qro}>Check the official rules</a>.
          </p>
          <section className="quick-duty" aria-live="polite" aria-label="Quick stamp duty estimate">
            <span className="eyebrow">Quick stamp duty estimate</span>
            {!quickDutyReady && <p>Complete transaction type, contract date, duty category and AFAD status to see duty before entering the detailed acquisition budget.</p>}
            {dutyBusy && <p>Calculating duty…</p>}
            {dutyPreview && <>
              <div><span>Transfer duty</span><strong>{money(dutyPreview.total_duty)}</strong></div>
              {dutyPreview.afad > 0 && <p>Includes {money(dutyPreview.afad)} additional foreign acquirer duty.</p>}
              <p className="source">Uses {money(dutyPreview.dutiable_value)} as the provisional dutiable value.{dutyPreview.conditional_concession ? " This is a conditional concession estimate; eligibility has not been assessed." : " General rates selected."} <a href={dutyPreview.source_url}>Verify with QRO</a>.</p>
            </>}
          </section>
          <h3>Your acquisition budget</h3>
          <p>
            Dutiable value may differ from the asking price. Confirm it before
            calculating. This calculator does not support a dutiable value below
            the purchase price.
          </p>
          <div className="form-grid">
            {fields.map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  required
                  name={key}
                  type="number"
                  min={key === "dutiable_value" ? price : 0}
                  max={key === "deposit" ? price : 1000000000}
                  step="0.01"
                  value={values[key] ?? ""}
                  onChange={update}
                />
              </label>
            ))}
          </div>
          <label className="checkbox">
            <input
              required
              type="checkbox"
              name="dutiable_value_confirmed"
              checked={values.dutiable_value_confirmed}
              onChange={update}
            />
            I have confirmed the dutiable value using QRO guidance or my
            conveyancer.
          </label>
          <button disabled={busy}>
            {busy ? "Calculating purchase costs…" : "Calculate purchase costs"}
          </button>
        </fieldset>
        {error && <p role="alert">{error}</p>}
      </form>
      {result && (
        <section aria-label="Purchase cost results" aria-live="polite">
          <h3>Your purchase cost scenario</h3>
          <p className="source">
            Calculated from your inputs · rules checked{" "}
            {result.rules_checked_on} · {result.rules_version}
          </p>
          <dl>
            {Object.entries(resultLabels).map(([key, label]) => (
              <div className="result-row" key={key}>
                <dt>
                  {label}
                  {["base_duty", "afad", "total_duty"].includes(key) && (
                    <>
                      <br />
                      <a href={result.source_url}>
                        Check with QRO’s official calculator
                      </a>
                    </>
                  )}
                </dt>
                <dd>{money(result.costs[key])}</dd>
              </div>
            ))}
          </dl>
          <CalculationDetails result={result} />
          {result.limitations.map((text) => (
            <p className="source" key={text}>
              {text}
            </p>
          ))}
          {propertyType !== "land" && (
            <p>
              Your deposit, acquisition costs and contingency are now linked to
              the cash-flow form below. Duty is included once.
            </p>
          )}
        </section>
      )}
    </details>
  );
}
